import React, { createContext, useContext, useState, useCallback, useRef, useEffect, ReactNode } from "react";

interface RoomUpdateContextValue {
  startUpdatingRoom: (patientId: string | number) => void;
  stopUpdatingRoom: (patientId: string | number) => void;
  isUserUpdatingRoom: (patientId: string | number) => boolean;
  startUpdatingPIF: (patientId: string | number) => void;
  stopUpdatingPIF: (patientId: string | number) => void;
  isUserUpdatingPIF: (patientId: string | number) => boolean;
}

interface RoomUpdateProviderProps {
  children: ReactNode;
}

const RoomUpdateContext = createContext<RoomUpdateContextValue | undefined>(undefined);

export const useRoomUpdateContext = (): RoomUpdateContextValue | null => {
  const context = useContext(RoomUpdateContext);
  return context || null;
};

export const RoomUpdateProvider = ({ children }: RoomUpdateProviderProps) => {
  const [updatingRoomPatients, setUpdatingRoomPatients] = useState<Set<string>>(new Set());
  const [updatingPIFPatients, setUpdatingPIFPatients] = useState<Set<string>>(new Set());
  const pifStopTimeouts = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const startUpdatingRoom = useCallback((patientId: string | number) => {
    setUpdatingRoomPatients((prev) => new Set(prev).add(String(patientId)));
  }, []);

  const stopUpdatingRoom = useCallback((patientId: string | number) => {
    setUpdatingRoomPatients((prev) => {
      const newSet = new Set(prev);
      newSet.delete(String(patientId));
      return newSet;
    });
  }, []);

  const isUserUpdatingRoom = useCallback(
    (patientId: string | number): boolean => {
      return updatingRoomPatients.has(String(patientId));
    },
    [updatingRoomPatients]
  );

  const startUpdatingPIF = useCallback((patientId: string | number) => {
    const key = String(patientId);
    // Cancel any pending delayed stop for this patient so it doesn't clear prematurely
    const pendingStop = pifStopTimeouts.current.get(key);
    if (pendingStop) {
      clearTimeout(pendingStop);
      pifStopTimeouts.current.delete(key);
    }
    setUpdatingPIFPatients((prev) => new Set(prev).add(key));
  }, []);

  const stopUpdatingPIF = useCallback((patientId: string | number) => {
    const key = String(patientId);
    const existing = pifStopTimeouts.current.get(key);
    if (existing) clearTimeout(existing);
    // Delay clearing the flag so the WebSocket event (Django -> SQS -> Saya -> Redis -> WS)
    // has time to arrive and be suppressed on the updater's session
    const timeoutId = setTimeout(() => {
      setUpdatingPIFPatients((prev) => {
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });
      pifStopTimeouts.current.delete(key);
    }, 5000);
    pifStopTimeouts.current.set(key, timeoutId);
  }, []);

  const isUserUpdatingPIF = useCallback(
    (patientId: string | number): boolean => {
      return updatingPIFPatients.has(String(patientId));
    },
    [updatingPIFPatients]
  );

  useEffect(() => {
    return () => {
      pifStopTimeouts.current.forEach((timeoutId) => clearTimeout(timeoutId));
      pifStopTimeouts.current.clear();
    };
  }, []);

  return (
    <RoomUpdateContext.Provider
      value={{ startUpdatingRoom, stopUpdatingRoom, isUserUpdatingRoom, startUpdatingPIF, stopUpdatingPIF, isUserUpdatingPIF }}>
      {children}
    </RoomUpdateContext.Provider>
  );
};
